import { useUser } from '../contexts/UserContext';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import { FcGoogle } from 'react-icons/fc';

import ParticleBackground from '../components/ParticleBackground';
import { useTranslation } from '../contexts/TranslationContext';
import { getApiErrorLabel } from '../utils/apiErrors';
import '../styles/JoinOrganization.scss';
import { API_BASE } from '../config/api';


const JoinOrganization = () => {
  const { refreshUser } = useUser(); // ✅ MOVE HERE
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Invite validation state
  const [inviteInfo, setInviteInfo] = useState(null);
  const [validating, setValidating] = useState(true);
  const [validationError, setValidationError] = useState('');

  // UI mode: 'choose' | 'register' | 'login'
  const [mode, setMode] = useState('choose');

  // Form fields
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Login fields
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials' | 'otp'

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // ── Step 1: Validate the token on mount ────────────────────────────────────
  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValidationError('No invite token found in URL. Please check your invite link.');
      return;
    }

    const validateToken = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/org/invite/${token}`);
        const data = await res.json();

        if (!res.ok) {
          setValidationError(data.error || 'Invalid invite link');
        } else {
          setInviteInfo(data.invite);
          // Pre-fill the login form's email (register form reads inviteInfo.email directly)
          setLoginEmail(data.invite.email);
        }
      } catch (_) {
        setValidationError('Could not connect to the server. Please try again later.');
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  // ── Shared: persist JWT and redirect ──────────────────────────────────────
  const finalizeLogin = async (jwt) => {
    localStorage.setItem('token', jwt);

    await refreshUser(); // ✅ now valid

    setSuccess(t('joinWelcome', { orgName: inviteInfo?.orgName || '' }));
    setTimeout(() => navigate('/profile'), 2000);
  };

  // ── Accept invite (works for both new and existing logged-in users) ────────
  const acceptInvite = async (jwt) => {
    const headers = { 'Content-Type': 'application/json' };
    if (jwt) headers['x-auth-token'] = jwt;

    const res = await fetch(`${API_BASE}/api/org/accept-invite`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token })
    });
    return res.json();
  };

  // ── GOOGLE flow (works for both new and existing users) ────────────────────
  const googleJoin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError('');
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/api/org/accept-invite`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, googleAccessToken: tokenResponse.access_token })
        });
        const data = await res.json();

        if (!res.ok) {
          setError(res.status === 403 && data.invitedEmail
            ? t('inviteEmailMismatch')
            : getApiErrorLabel(t, data, 'googleLoginFailed'));
          return;
        }

        finalizeLogin(data.token);
      } catch (_) {
        setError(t('errNetwork'));
      } finally {
        setLoading(false);
      }
    },
    onError: () => setError(t('googleLoginFailedShort')),
  });

  const GoogleJoinButton = () => (
    <button
      type="button"
      className="google-btn"
      onClick={() => googleJoin()}
      disabled={loading}
      style={{ width: '100%' }}
    >
      <FcGoogle size={22} />
      <span>{t('loginWithGoogle')}</span>
    </button>
  );

  // ── REGISTER flow ──────────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmPassword) {
      setError(t('errPasswordsMismatch'));
      return;
    }
    if (password.length < 8) {
      setError(t('errAuthPasswordTooShort'));
      return;
    }
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/org/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, name, password })
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.requiresLogin) {
          setError(getApiErrorLabel(t, data));
          setLoginEmail(inviteInfo.email);
          setMode('login');
        } else {
          setError(getApiErrorLabel(t, data));
        }
        return;
      }

      finalizeLogin(data.token);
    } catch (_) {
      setError(t('errNetwork'));
    } finally {
      setLoading(false);
    }
  };

  // ── LOGIN flow ─────────────────────────────────────────────────────────────
  const handleLoginCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await res.json();

      if (!res.ok) {
        setError(getApiErrorLabel(t, data));
        return;
      }

      // Login sends OTP — move to OTP step
      if (data.message && data.message.toLowerCase().includes('otp')) {
        setLoginStep('otp');
      } else if (data.token) {
        // Directly logged in (Google or password-reset bypass)
        await handleAcceptAfterLogin(data.token);
      }
    } catch (_) {
      setError(t('errNetwork'));
    } finally {
      setLoading(false);
    }
  };

  const handleLoginOtp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, otp: loginOtp })
      });
      const data = await res.json();

      if (!res.ok) {
        setError(getApiErrorLabel(t, data));
        return;
      }

      await handleAcceptAfterLogin(data.token);
    } catch (_) {
      setError(t('errNetwork'));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptAfterLogin = async (jwt) => {
    const data = await acceptInvite(jwt);
    if (data.success) {
      finalizeLogin(data.token || jwt);
    } else {
      // Already a member or other non-fatal issue
      setError(getApiErrorLabel(t, data));
      if (data.code === 'ORG_ALREADY_MEMBER') {
        localStorage.setItem('token', jwt);
        setTimeout(() => navigate('/profile'), 2000);
      }
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────

  if (validating) {
    return (
      <div className="join-page">
        <ParticleBackground />
        <div className="join-card">
          <div className="join-logo">FORTEXA</div>
          <div className="join-loading">
            <div className="spinner" />
            <p>Validating invite link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="join-page">
        <ParticleBackground />
        <div className="join-card">
          <div className="join-logo">FORTEXA</div>
          <div className="join-error-box">
            <div className="join-error-icon">✕</div>
            <h2>{t('joinInviteInvalidTitle')}</h2>
            <p>{validationError}</p>
            <button className="join-btn" onClick={() => navigate('/')}>{t('joinGoHome')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="join-page">
      <ParticleBackground />
      <div className="join-card">
        <div className="join-logo">FORTEXA</div>

        {/* Invite Summary Banner */}
        <div className="join-invite-banner">
          <p className="join-invite-label">You're invited to join</p>
          <h2 className="join-org-name">{inviteInfo.orgName}</h2>
          <span className="join-role-badge">{inviteInfo.role.toUpperCase()}</span>
        </div>

        {success && <div className="join-success">{success}</div>}
        {error && <div className="join-error">{error}</div>}

        {/* ── Mode: Choose ── */}
        {mode === 'choose' && (
          <div className="join-choose">
            <p className="join-subtitle">How would you like to join?</p>
            <GoogleJoinButton />
            <div className="join-or-separator"><span>{t('or')}</span></div>
            <button className="join-btn join-btn--primary" onClick={() => setMode('register')}>
              Create New Account
            </button>
            <button className="join-btn join-btn--secondary" onClick={() => setMode('login')}>
              Log In to Existing Account
            </button>
          </div>
        )}

        {/* ── Mode: Register ── */}
        {mode === 'register' && (
          <form className="join-form" onSubmit={handleRegister}>
            <h3>Create Your Account</h3>
            <p className="join-email-notice">Joining as: <strong>{inviteInfo.email}</strong></p>

            <div className="join-field">
              <label>Full Name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Your full name"
                required
                autoFocus
              />
            </div>
            <div className="join-field">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Create a password"
                required
              />
            </div>
            <div className="join-field">
              <label>Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Repeat password"
                required
              />
            </div>

            <button type="submit" className="join-btn join-btn--primary" disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account & Join'}
            </button>
            <div className="join-or-separator"><span>{t('or')}</span></div>
            <GoogleJoinButton />
            <button type="button" className="join-btn--link" onClick={() => setMode('choose')}>
              ← Back
            </button>
          </form>
        )}

        {/* ── Mode: Login ── */}
        {mode === 'login' && loginStep === 'credentials' && (
          <form className="join-form" onSubmit={handleLoginCredentials}>
            <h3>Log In to Join</h3>
            <div className="join-field">
              <label>Email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                placeholder="your@email.com"
                required
              />
            </div>
            <div className="join-field">
              <label>Password</label>
              <input
                type="password"
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                placeholder="Your password"
                required
              />
            </div>

            <button type="submit" className="join-btn join-btn--primary" disabled={loading}>
              {loading ? 'Logging in...' : 'Log In & Join'}
            </button>
            <div className="join-or-separator"><span>{t('or')}</span></div>
            <GoogleJoinButton />
            <button type="button" className="join-btn--link" onClick={() => setMode('choose')}>
              ← Back
            </button>
          </form>
        )}

        {/* ── Mode: Login OTP ── */}
        {mode === 'login' && loginStep === 'otp' && (
          <form className="join-form" onSubmit={handleLoginOtp}>
            <h3>Enter Verification Code</h3>
            <p className="join-subtitle">We sent a code to <strong>{loginEmail}</strong></p>
            <div className="join-field">
              <label>OTP Code</label>
              <input
                type="text"
                value={loginOtp}
                onChange={e => setLoginOtp(e.target.value)}
                placeholder="6-digit code"
                maxLength={6}
                required
                autoFocus
              />
            </div>
            <button type="submit" className="join-btn join-btn--primary" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify & Join'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default JoinOrganization;
